import { config, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, XStack } from '@/components/ui';
import { IconButton } from '@/components/ui/IconButton';
import { useProjectActions } from '@/features/projects/project-actions';

import { USER_DID_SEND_MESSAGE_EVENT, localChatEventsBus } from './chat.events';
import { useTrackProjectSuggestionShown } from './use-track-project-suggestion-shown';

export const SuggestedProjectBanner = observer(
  ({
    threadId,
    isSurfaceVisible = true,
  }: {
    threadId: string;
    isSurfaceVisible?: boolean;
  }) => {
    const { threads: threadsStore } = useStores();
    const { acceptProjectSuggestion, dismissProjectSuggestion } =
      useProjectActions();
    const [isBusy, setIsBusy] = useState(false);

    const thread = threadsStore.getById(threadId);
    const suggestion = thread?.suggestedProject ?? null;
    const isVisible =
      isSurfaceVisible &&
      config.chat.project_suggestions_enabled &&
      !!thread &&
      !thread.projectId &&
      !!suggestion;

    useTrackProjectSuggestionShown(threadId, isVisible ? suggestion : null);

    useEffect(() => {
      if (!isVisible || isBusy) return;
      const listener = localChatEventsBus.on(
        USER_DID_SEND_MESSAGE_EVENT,
        ({ data }) => {
          if (
            data.threadId === threadId &&
            threadsStore.getById(threadId)?.suggestedProject
          ) {
            void dismissProjectSuggestion(threadId);
          }
        }
      );
      const timeout = window.setTimeout(() => {
        if (threadsStore.getById(threadId)?.suggestedProject) {
          void dismissProjectSuggestion(threadId);
        }
      }, 60_000);
      return () => {
        listener.unsubscribe();
        window.clearTimeout(timeout);
      };
    }, [dismissProjectSuggestion, isBusy, isVisible, threadId, threadsStore]);

    if (!isVisible || !suggestion) return null;

    const isExisting = suggestion.projectId !== null;

    const handleAccept = async () => {
      if (isBusy) return;
      setIsBusy(true);
      try {
        await acceptProjectSuggestion(threadId);
      } finally {
        setIsBusy(false);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="mb-2 overflow-hidden rounded-xl border border-border-strong bg-surface"
      >
        <XStack className="items-stretch">
          <XStack className="min-w-0 flex-1 items-center gap-2 py-2 pl-3 pr-2">
            <Icons.lightbulb className="size-3.5 flex-shrink-0 text-foreground-muted" />
            <span className="min-w-0 truncate text-sm text-foreground-muted">
              {isExisting ? 'Add this chat to ' : 'Start a new project '}
              <span className="font-medium text-foreground">
                {suggestion.name}
              </span>
              ?
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={isBusy}
              className="ml-auto flex-shrink-0"
              onClick={handleAccept}
            >
              {isExisting ? 'Add to project' : 'Create project'}
            </Button>
          </XStack>
          <XStack center className="shrink-0 pr-2">
            <IconButton
              icon={Icons.cancel}
              size="xs"
              variant="ghost"
              tooltip="Dismiss"
              disabled={isBusy}
              aria-label="Dismiss suggestion"
              onClick={() => void dismissProjectSuggestion(threadId)}
            />
          </XStack>
        </XStack>
      </motion.div>
    );
  }
);
